import { ModelPageConfig } from '../types';

const config: ModelPageConfig = {
  slug: 'gpt-image-2',
  modelName: 'GPT Image 2',
  heroOperationType: 'image',
  hero: {
    title: 'GPT Image 2 — AI That Gets Text Right',
    description:
      'People are sharing GPT Image 2 outputs and asking "wait, is this real?" — perfect text, no plastic filter, no warped hands. Just images good enough to fool the internet.',
    images: [
      'https://falcocut-assets-sgp-1375136936.cos.ap-singapore.myqcloud.com/1776857951496-5icl70.png',
      'https://falcocut-assets-sgp-1375136936.cos.ap-singapore.myqcloud.com/1776857951499-7yaet8.png',
      'https://falcocut-assets-sgp-1375136936.cos.ap-singapore.myqcloud.com/1776857951501-86j9z7.png',
    ],
  },
  features: {
    sectionTitle: 'Why Creators Choose GPT Image 2',
    items: [
      {
        title: 'Text That Actually Reads',
        description:
          'Finally, an AI that nails every word. GPT Image 2 renders small text, multilingual labels, and dense layouts with ~99% accuracy — no more gibberish in your banners or product shots.',
      },
      {
        title: 'Thinks Before It Draws',
        description:
          'GPT Image 2 is the first image model with built-in reasoning — it plans layout, composition, and context before generating a single pixel. The result? Images that look intentional, not accidental.',
      },
      {
        title: "Edit Like You're Texting",
        description:
          'Describe what to change in plain English — "swap the background to a sunset", "remove the logo on the left" — and GPT Image 2 updates only what you asked, leaving everything else intact.',
      },
    ],
  },
  whyChoose: {
    title: 'One Model. Every Industry.',
    description:
      'From beauty campaigns to tech launches, GPT Image 2 delivers production-ready visuals across every category — with accurate text, consistent style, and zero design skills required.',
    items: [
      {
        tag: 'Beauty & Skincare',
        heading: 'Campaign Visuals That Sell',
        description:
          'Generate premium beauty imagery that rivals studio photography — soft lighting, luxurious textures, and on-brand typography all in one prompt. No photographer, no retoucher, no waiting.',
        bullets: [
          'Perfect product shots with accurate label text',
          'Lifestyle and flat-lay variations from a single prompt',
        ],
        imageUrl:
          'https://falcocut-assets-sgp-1375136936.cos.ap-singapore.myqcloud.com/1776857956516-8250ue.png',
      },
      {
        tag: 'E-Commerce & Retail',
        heading: 'Product Images Without a Studio',
        description:
          'Turn a product description into a professional lifestyle shot or clean white-background image. GPT Image 2 understands context — it knows a skincare bottle should look premium, not generic.',
        bullets: [
          'Studio-quality product shots from text alone',
          'Multiple angles and backgrounds in one session',
        ],
        imageUrl:
          'https://falcocut-assets-sgp-1375136936.cos.ap-singapore.myqcloud.com/1776857956787-5wkt9z.png',
      },
      {
        tag: 'Tech & Design',
        heading: 'UI Mockups & Sci-Fi Visuals in Minutes',
        description:
          "Generate pixel-perfect app screens, futuristic product renders, and cinematic tech visuals. GPT Image 2 handles dense UI layouts and dramatic lighting effects that other models can't touch.",
        bullets: [
          'Accurate UI elements, icons, and small text',
          'Cinematic sci-fi and product render aesthetics',
        ],
        imageUrl:
          'https://falcocut-assets-sgp-1375136936.cos.ap-singapore.myqcloud.com/1776857956797-2c3dna.png',
      },
      {
        tag: 'Toys, IP & Social Content',
        heading: 'Characters, Carousels & Viral Creatives',
        description:
          'Bring IP characters to life, design collectible product shots, and build scroll-stopping social carousels — all with perfectly rendered text and consistent visual style across every frame.',
        bullets: [
          'Consistent character style across multiple images',
          'Social-ready layouts with legible text and bold colors',
        ],
        imageUrl:
          'https://falcocut-assets-sgp-1375136936.cos.ap-singapore.myqcloud.com/1776857960946-3uow5q.png',
      },
    ],
  },
  exploreModels: {
    title: 'Explore More AI Models on FalcoCut',
    models: [
      { name: 'Sora 2 Pro', href: '/models/sora-2-pro' },
      { name: 'Seedance 2.0', href: '/models/seedance-2-0' },
      { name: 'Kling V2.6 Pro', href: '/models/kling-v2-6-pro' },
      { name: 'FLUX Kontext', href: '/models/flux-kontext' },
    ],
  },
  toolkits: {
    title: 'FalcoCut AI Toolkits',
    description:
      'FalcoCut provides a full suite of AI tools for creating high-quality images and videos faster. From AI image generation and avatars to video creation, everything you need in one platform.',
    items: [
      {
        title: 'AI Image Generator',
        href: '/ai-image-generator',
        imageUrl:
          'https://falcocut-assets-sgp-1375136936.cos.ap-singapore.myqcloud.com/1776857964626-dzf8pq.png',
      },
      {
        title: 'AI Video Generator',
        href: '/ai-video-generator',
        imageUrl: 'https://assets.falcocut.ai/tools/common/ai-video-generator.png',
      },
      {
        title: 'AI Avatar Generator',
        href: '/ai-avatar-generator',
        imageUrl: 'https://assets.falcocut.ai/tools/common/ai-avatar-generator.png',
      },
      {
        title: 'Product Avatar',
        href: '/ai-product-avatar',
        imageUrl: 'https://assets.falcocut.ai/tools/common/product-avatar.png',
      },
      {
        title: 'AI Video Editor',
        href: '/ai-video-editor',
        imageUrl: 'https://assets.falcocut.ai/tools/common/ai-video-editor.png',
      },
    ],
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'What is GPT Image 2?',
        answer:
          '<p>GPT Image 2 is an advanced AI image generation model developed by OpenAI that creates high-quality, photorealistic images from text prompts. It understands complex descriptions and renders detailed scenes with accurate lighting, textures, and compositions. The model supports a wide range of visual styles from photorealistic to illustrated. It is designed to make professional-grade image creation accessible without requiring design skills.</p>',
      },
      {
        question: 'How do I use GPT Image 2 on FalcoCut?',
        answer:
          '<ol><li><strong>Step 1:</strong> Select GPT Image 2 inside FalcoCut to activate the image generation feature.</li><li><strong>Step 2:</strong> Enter a detailed text prompt describing the image you want to create.</li><li><strong>Step 3:</strong> Choose your preferred aspect ratio and quality settings.</li><li><strong>Step 4:</strong> Click Generate and download your image once it is ready.</li></ol>',
      },
      {
        question: 'What types of images can GPT Image 2 create?',
        answer:
          '<p>GPT Image 2 can generate a wide range of images including photorealistic portraits, product visuals, landscapes, abstract art, and stylized illustrations. It handles both simple and complex prompts with multiple elements. The model is suitable for marketing assets, social media content, e-commerce product images, and creative projects. Its versatility makes it useful across industries and use cases.</p>',
      },
      {
        question: 'Is GPT Image 2 free to use on FalcoCut?',
        answer:
          '<p>FalcoCut offers access to GPT Image 2 with a free tier that includes a limited number of generations. Users can try the model and explore its capabilities without upfront cost. Advanced features, higher resolution outputs, and increased generation limits are available on paid plans. This structure allows creators to start immediately and scale as their needs grow.</p>',
      },
      {
        question: 'Can I use GPT Image 2 for commercial projects?',
        answer:
          "<p>Yes. Images generated with GPT Image 2 on FalcoCut can be used for commercial purposes including marketing campaigns, product listings, and branded content. Users retain the rights to images they generate within the platform's terms of service. It is recommended to review FalcoCut's usage policies for specific commercial use cases. This makes GPT Image 2 a practical tool for businesses and professional creators.</p>",
      },
    ],
  },
  cta: {
    title: 'Stop Settling for Blurry Text and Generic Visuals',
    description:
      'GPT Image 2 on FalcoCut gives you 4K images with perfect text rendering — ready for ads, products, and social media. Try it free, no credit card needed.',
    buttonText: 'Generate Your First Image Free',
  },
  bottomCta: {
    title: 'Ready to Create Stunning Images?',
    description: 'Join thousands of creators using GPT Image 2 on FalcoCut.',
    buttonText: 'Get Started Free',
  },
  seo: {
    title: 'GPT Image 2 AI Image Generator - Create Stunning Images | FalcoCut',
    description:
      'Generate high-quality AI images with GPT Image 2 on FalcoCut. Transform text prompts into photorealistic images for marketing, social media, and creative projects. Try free today.',
    keywords: 'GPT Image 2, AI image generator, text to image, AI art generator',
    publishedTime: '2026-04-22T00:00:00Z',
    modifiedTime: '2026-04-22T00:00:00Z',
  },
};

export default config;
